import { Link } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import paths from '@/paths';
import type { SpaBuyerCodesRecord } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import {
  needsDuns,
  resubmitDunsVerdict,
  type ResubmitDunsVerdict,
  type SpaDunsComparison,
} from './spaDunsComparison';

/** Each SPA record as a link to its record page, with its codes and window. */
export function SpaRecordLinks({ spas }: { spas: SpaBuyerCodesRecord[] }) {
  return (
    <ul className="mt-1 space-y-0.5">
      {spas.map((spa) => (
        <li key={spa.spaId} className="text-xs">
          <Link
            to={paths.authenticated.pricing.spaRecordDetail}
            params={{ spaId: spa.spaId }}
            target="_blank"
            className="text-blue-600 hover:text-blue-800 underline underline-offset-2"
          >
            <span className="font-mono">{spa.codes.join(', ')}</span>{' '}
            <span>
              ({spa.startDate} → {spa.endDate})
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Inside the rejection-code banner: the DUNS we submitted beside the DUNS on
 * every SPA for the contract that was active on the invoice date, each linked
 * to its SPA record, so a code-1 rejection caused by the wrong branch DUNS
 * reads at a glance.
 */
export function SpaDunsCheck({
  comparison,
}: {
  comparison: SpaDunsComparison;
}) {
  const { submitted, asOf, activeSpas, spaCodes, spasOnFile, matches } =
    comparison;
  return (
    <div className="mt-2 pt-2 border-t border-red-200/60 grid grid-cols-2 gap-3">
      <div>
        <p className="text-[11px] text-muted-foreground">
          <T context="849 rejection banner label for the buyer DUNS we sent on the 844">
            DUNS submitted
          </T>
        </p>
        <p
          className={`text-sm font-mono font-medium ${matches ? 'text-foreground' : 'text-red-800'}`}
        >
          {submitted ?? '—'}
        </p>
      </div>
      <div>
        <p className="text-[11px] text-muted-foreground">
          {asOf ? (
            <T context="849 rejection banner label for the buyer DUNS on the SPAs active on the claim's invoice date">
              DUNS on SPAs active <Var>{asOf}</Var>
            </T>
          ) : (
            <T context="849 rejection banner label for the buyer DUNS on the SPAs on file (claim has no invoice date)">
              DUNS on SPAs on file
            </T>
          )}
        </p>
        {activeSpas.length > 0 ? (
          <>
            <p className="text-sm font-mono font-medium text-green-800">
              {spaCodes.join(', ')}
            </p>
            <SpaRecordLinks spas={activeSpas} />
          </>
        ) : (
          <>
            <p className="text-sm text-red-800">
              <T context="849 rejection banner: no SPA for the contract was active on the claim's invoice date">
                No SPA active on that date. On file:
              </T>
            </p>
            <SpaRecordLinks spas={spasOnFile} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * In the resubmit edit modal, under the DUNS field: the DUNS on every SPA for
 * the contract active on the invoice date, each linked to its SPA record,
 * green once the DUNS being resent is one of them. Without an invoice date it
 * lists every SPA on file, neutrally — there is no day to judge a match on.
 */
export function ResubmitSpaDuns({
  comparison,
  filledIn,
  onUseDuns,
}: {
  comparison: SpaDunsComparison;
  /** The DUNS field holds the DUNS the editor filled in from the SPA on open. */
  filledIn: boolean;
  /** Put one of the SPA's DUNS in the DUNS field. */
  onUseDuns: (duns: string) => void;
}) {
  const { submitted, asOf, activeSpas, spaCodes, spasOnFile } = comparison;
  const verdict = resubmitDunsVerdict(comparison);
  const tone = RESUBMIT_DUNS_TONE[verdict];
  return (
    <div className={`rounded-lg border px-3 py-2 ${tone.box}`}>
      <p className="text-[11px] text-muted-foreground">
        {asOf ? (
          <T context="resubmit edit modal label for the buyer DUNS on the SPAs active on the claim's invoice date">
            DUNS on SPAs active <Var>{asOf}</Var>
          </T>
        ) : (
          <T context="resubmit edit modal label for the buyer DUNS on the SPAs on file (claim has no invoice date)">
            DUNS on SPAs on file
          </T>
        )}
      </p>
      {verdict === 'noActiveSpa' ? (
        <>
          <p className="text-sm text-red-800">
            <T context="resubmit edit modal: no SPA for the contract was active on the claim's invoice date">
              No SPA active on that date. On file:
            </T>
          </p>
          <SpaRecordLinks spas={spasOnFile} />
        </>
      ) : (
        <>
          <p className={`text-sm font-mono font-medium ${tone.text}`}>
            {spaCodes.join(', ')}
          </p>
          {/* Stays mounted so screen readers hear the verdict change as the
              DUNS is typed. */}
          <p aria-live="polite" className={`text-xs ${tone.text}`}>
            <ResubmitDunsVerdictText verdict={verdict} submitted={submitted} />
          </p>
          {needsDuns(verdict) && (
            <UseDunsButtons codes={spaCodes} onUseDuns={onUseDuns} />
          )}
          {filledIn && (
            <p className="text-xs text-muted-foreground">
              <T context="resubmit edit modal: the DUNS field was filled in from the SPA; edits are only sent on resubmit">
                Filled in from the SPA. Nothing is sent until you resubmit.
              </T>
            </p>
          )}
          <SpaRecordLinks spas={activeSpas} />
        </>
      )}
    </div>
  );
}

const RESUBMIT_DUNS_TONE: Record<
  ResubmitDunsVerdict,
  { box: string; text: string }
> = {
  unjudged: { box: 'border-border bg-muted/30', text: 'text-foreground' },
  noActiveSpa: { box: 'border-red-200 bg-red-50/60', text: 'text-red-800' },
  match: { box: 'border-green-200 bg-green-50/60', text: 'text-green-800' },
  mismatch: { box: 'border-red-200 bg-red-50/60', text: 'text-red-800' },
  blank: { box: 'border-red-200 bg-red-50/60', text: 'text-red-800' },
};

function UseDunsButtons({
  codes,
  onUseDuns,
}: {
  codes: string[];
  onUseDuns: (duns: string) => void;
}) {
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {codes.map((code) => (
        <Button
          key={code}
          type="button"
          size="sm"
          variant="outline"
          className="h-6 px-2 text-xs"
          onClick={() => onUseDuns(code)}
        >
          <T context="resubmit edit modal button: put this SPA DUNS in the DUNS field">
            Use <Var>{code}</Var>
          </T>
        </Button>
      ))}
    </div>
  );
}

function ResubmitDunsVerdictText({
  verdict,
  submitted,
}: {
  verdict: ResubmitDunsVerdict;
  submitted: string | null;
}) {
  switch (verdict) {
    case 'match':
      return (
        <T context="resubmit edit modal: the DUNS being resent is on an active SPA">
          DUNS <Var>{submitted}</Var> is on an active SPA.
        </T>
      );
    case 'mismatch':
      return (
        <T context="resubmit edit modal: the DUNS being resent is not on any active SPA">
          DUNS <Var>{submitted}</Var> is not on these SPAs.
        </T>
      );
    case 'blank':
      return (
        <T context="resubmit edit modal: the DUNS field is blank">
          Enter one of these DUNS.
        </T>
      );
    default:
      return null;
  }
}
